import { INFO_POPUP_TYPE, InfoPopup, useAccountContext } from '@apeiron/temp'
import { MuiCheckbox } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import ApeironWalletAddressInput from '@src/components/share/apeiron/ApeironWalletAddressInput'
import TradeQuanatitySection from '@src/components/share/trade/TradeQuantitySection'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { TradeItem } from '@src/types/trade'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import * as R from 'ramda'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Address, isAddress } from 'viem'

const Popup = styled(InfoPopup)`
  max-height: 95vh;

  .Custom-Content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0px 25px 30px 25px;
  }
`

const DialogTitle = styled(Typography)`
  font-size: 24px;
  font-weight: 400;
  line-height: 24px;
`

const DialogDescription = styled(Typography)`
  color: #b1b3b3;
  font-size: 14px;
  font-weight: 300;
`

const WalletAddressInput = styled(ApeironWalletAddressInput)`
  margin-top: 10px;
`

const StyleCheckBox = styled(MuiCheckbox)`
  padding: 0;
`

const CheckBoxContainer = styled(Box)`
  display: flex;
  justify-items: center;
  align-items: center;
`

const CheckBoxDescription = styled(Box)`
  margin-left: 12px;
  font-size: 16px;
  line-height: 18px;
  color: #b1b3b3;
`

const StyledConfirmButton = styled(ApeironConfirmButton)`
  margin-top: 10px;
  align-self: flex-end;
`

const TradeGiftDialog: FC<Props> = props => {
  const {
    checkboxRemark,
    className,
    description,
    isOpened,
    item,
    loading,
    title,
    totalQuantity = -1,
    onConfirm,
    onClose
  } = props

  const { t } = useTranslation()

  const { account } = useAccountContext()

  const [quantity, setQuantity] = useState<number>(1)

  const [walletAddress, setWalletAddress] = useState<string>('')

  const [addressValid, setAddressValid] = useState<boolean>(false)

  const [isChecked, setIsChecked] = useState(
    !featureAllowed(FeatureFlagType.ShowTradeRemark)
  )

  const handleOnQuantityChange = useCallback((value: number) => {
    setQuantity(value)
  }, [])

  const handleOnAddressChange = useCallback((address: string) => {
    setWalletAddress(address)
  }, [])

  const handleOnCheckAddress = useCallback((valid: boolean) => {
    setAddressValid(valid)
  }, [])

  const handleOnReset = useCallback(() => {
    setWalletAddress('')
    setQuantity(1)
    setIsChecked(true)
  }, [])

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose()
    }

    handleOnReset()
  }, [handleOnReset, onClose])

  const handleOnConfirm = useCallback(async () => {
    if (onConfirm && isAddress(walletAddress)) {
      const success = await onConfirm(item, walletAddress, quantity)

      if (success) {
        handleOnReset()
      }
    }
  }, [onConfirm, item, walletAddress, quantity, handleOnReset])

  const handleOnCheckBoxChange = useCallback(() => {
    setIsChecked(prev => !prev)
  }, [])

  const confirmDisabled = useMemo((): boolean => {
    if (!isChecked || !addressValid) {
      return true
    }

    // make sure that input address is not you
    if (walletAddress.toLowerCase() === account.walletAddress?.toLowerCase()) {
      return true
    }

    if (totalQuantity !== -1 && (quantity > totalQuantity || quantity < 1)) {
      return true
    }

    return false
  }, [
    account.walletAddress,
    addressValid,
    isChecked,
    quantity,
    totalQuantity,
    walletAddress
  ])

  const onConditionalClose = loading ? () => {} : handleOnClose

  return (
    <Popup
      className={className}
      type={INFO_POPUP_TYPE.BLACK}
      open={isOpened}
      onClose={onConditionalClose}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>{description}</DialogDescription>
      {totalQuantity !== -1 && (
        <TradeQuanatitySection
          quantity={quantity}
          totalQuantity={totalQuantity}
          onChange={handleOnQuantityChange}
        />
      )}
      <WalletAddressInput
        allowUnknownUser
        autoFocus
        disabled={loading}
        placeholder={t('dialog.gift.address_placeholder')}
        onChangeAddress={handleOnAddressChange}
        onCheckAddress={handleOnCheckAddress}
      />
      {featureAllowed(FeatureFlagType.ShowTradeRemark) &&
        !R.isNil(checkboxRemark) && (
          <CheckBoxContainer>
            <StyleCheckBox
              checked={isChecked}
              disabled={loading}
              onChange={handleOnCheckBoxChange}
            />
            <CheckBoxDescription>{checkboxRemark}</CheckBoxDescription>
          </CheckBoxContainer>
        )}
      <StyledConfirmButton
        onClick={handleOnConfirm}
        loading={loading}
        disabled={confirmDisabled}
      />
    </Popup>
  )
}

type Props = {
  checkboxRemark?: string
  className?: string
  description: string
  isOpened: boolean
  item: TradeItem
  loading: boolean
  title: string
  totalQuantity?: number // -1 means no quantity input
  onClose?: () => void
  onConfirm?: (
    gift: TradeItem,
    walletAddress: Address,
    quantity: number
  ) => Promise<boolean>
}

export default TradeGiftDialog
